import {
  pinzielSchluessel,
  PULS_LOSLASSEN_BEI,
  type Aufgabengroesse,
  type Pinziel,
  type TaskStatus,
  type Zeitfenster,
} from '@netcon-work/shared'
import { useMutation, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query'
import {
  aufgabeDetailQuery,
  aufgabenQuery,
  poolsQuery,
  schirmQuery,
  sitzungQuery,
  type Aufgabe,
  type AufgabeDetailDaten,
  type EingangZeile,
  type Pool,
  type Schirm,
  type Sitzung,
} from './queries'
import { pulsNachStatus } from './puls'
import { liveKanalVerbunden } from './realtime'
import { trpc } from './trpc'

/**
 * Die Handlungen, die sofort wirken müssen: **Ziehen, Status setzen, Zuweisen**
 * (Phase 4 AP 4.7) und seit dem 21.08.2026 die **Planungsfelder des
 * Aufgabendetails** – Zeitfenster, Größe, „kommen in Frage", Pools (Leitplanke 1
 * – keine Standardaktion zeigt einen Spinner).
 *
 * ## Warum das hier liegt und nicht in den Ansichten
 *
 * Eine optimistische Änderung ist eine **Vorhersage der Serverantwort**. Ziehen
 * betrifft zwei Zwischenspeicher (der Bestand *und* die Pools) und drei Felder,
 * und dieselbe Handlung gibt es an drei Stellen der Oberfläche. Stünde die
 * Vorhersage dreimal da, liefe sie auseinander – und eine falsche Vorhersage ist
 * schlimmer als ein Spinner, weil sie nach Tatsache aussieht.
 *
 * Jede Vorhersage hier ist gegen die zugehörige Prozedur in
 * `apps/api/src/trpc/routers/tasks.ts` geschrieben. Wer dort etwas ändert, muss
 * hier nachziehen.
 *
 * ## Warum nach der Antwort nicht neu geladen wird
 *
 * Der übliche Reflex ist `invalidateQueries` in `onSettled`. Das wäre hier ein
 * Fehler: der Server verschickt nach jeder Änderung `aufgabe.geaendert` über den
 * WebSocket, und die Shell lädt darauf ohnehin nach. Beides zusammen holt den
 * **kompletten Aufgabenbestand zweimal** – bei 1.847 Aufgaben ist das keine
 * Kleinigkeit, und die Ansicht flackert dabei.
 *
 * Der Preis: wenn der Socket getrennt ist, bleibt die Bestätigung aus. Deshalb
 * lädt die Shell beim **Wiederverbinden** alles nach (`AppShell.tsx`) – sonst
 * wäre das hier eine stille Lücke.
 *
 * ## Was bei einem Konflikt passiert
 *
 * Zurückgerollt wird auf den Schnappschuss von vor dem Klick, und der Fehler
 * bleibt an der Mutation stehen, damit die Ansicht ihn zeigen kann. Ohne Hinweis
 * wäre der Rücksetzer für den Nutzer nicht erklärbar: die Karte war weg und ist
 * wieder da.
 */

/**
 * Die Felder, die eine optimistische Änderung an einer Aufgabe anfassen darf.
 *
 * `workTakenAt` ist eine **Zeichenkette**, kein `Date`: ohne Transformer geht über
 * tRPC JSON, und der Client-Typ sagt das auch so. Ein `Date` hier wäre ein Wert,
 * den derselbe Zwischenspeicher nach dem Nachladen nicht mehr hätte – und der
 * Unterschied fiele erst dort auf, wo jemand ihn formatiert.
 */
interface Flicken {
  status?: TaskStatus
  assigneeId?: string | null
  assigneeName?: string | null
  /**
   * **Der Bearbeiter, den Work führt** – getrennt vom wirksamen ([Z-06](../../../../docs/phasen/zusatz-06-nehmen-und-starten.md)).
   *
   * Muss mit vorhergesagt werden, weil daran hängt, ob an einer Zeile „Nehmen" oder „Starten"
   * steht. Nur `assigneeId` zu flicken hieße: nach dem Klick steht mein Name da, die Marke sagt
   * weiter „herrenlos" – und die Vorhersage beantwortet eine andere Frage als die Ansicht.
   */
  workAssigneeId?: string | null
  workTakenAt?: string | null
  /** Die Wiedervorlage beim Warten (E-315/E-357) – sonst zeigt „Nachfassen" kurz das alte Datum. */
  wartenBis?: string | null
}

/**
 * **Der Eingang ist eine eigene Liste, kein Ausschnitt des Bestands.**
 *
 * `eingang.liste` fragt der Server nach `imEingang` (`packages/db/src/effektiv.ts`):
 * kein Pool, kein Zeitfenster, niemand, der in Frage kommt. Eine Aufgabe verlässt
 * die Liste also, sobald jemand eines dieser drei setzt – aber der Zwischenspeicher
 * hier heißt `['eingang', 'liste', …]` und wird von keinem Flicken am Bestand
 * berührt.
 *
 * Es gibt **vier** davon, einen je Sortierung. Deshalb `setQueriesData` über das
 * Präfix und nicht `setQueryData` auf einen Schlüssel: welche Sortierung gerade
 * offen ist, weiß eine Mutation nicht, und die anderen drei lägen sonst mit einer
 * Zeile da, die es nicht mehr gibt.
 */
const EINGANG_LISTE = { queryKey: ['eingang', 'liste'] } as const

/**
 * Was ein Rücksetzer braucht: die Zwischenspeicher, wie sie vor dem Klick
 * standen. Bewusst die ganzen Listen und nicht „das alte Feld" – wer nur das
 * geänderte Feld merkt, kann eine Aufgabe nicht wieder in ihre Pools legen.
 */
interface Schnappschuss {
  aufgabeId: string
  aufgaben: Aufgabe[] | undefined
  pools: Pool[] | undefined
  detail: AufgabeDetailDaten | undefined
  /** Je Sortierung eine Liste – siehe `EINGANG_LISTE`. */
  eingang: [QueryKey, EingangZeile[] | undefined][]
}

function schnappschuss(qc: QueryClient, aufgabeId: string): Schnappschuss {
  return {
    aufgabeId,
    aufgaben: qc.getQueryData(aufgabenQuery.queryKey),
    pools: qc.getQueryData(poolsQuery.queryKey),
    detail: qc.getQueryData(aufgabeDetailQuery(aufgabeId).queryKey),
    eingang: qc.getQueriesData<EingangZeile[]>(EINGANG_LISTE),
  }
}

function zurueckrollen(qc: QueryClient, vorher: Schnappschuss | undefined): void {
  if (!vorher) return
  qc.setQueryData(aufgabenQuery.queryKey, vorher.aufgaben)
  qc.setQueryData(poolsQuery.queryKey, vorher.pools)
  qc.setQueryData(aufgabeDetailQuery(vorher.aufgabeId).queryKey, vorher.detail)
  for (const [schluessel, zeilen] of vorher.eingang) qc.setQueryData(schluessel, zeilen)
}

/**
 * Laufende Abfragen anhalten, bevor geflickt wird.
 *
 * Ohne das kann eine Antwort, die vor dem Klick losgeschickt wurde, danach
 * eintreffen und die optimistische Änderung überschreiben – die Aufgabe wäre
 * wieder da, obwohl sie gezogen ist.
 *
 * **Das Detail gehört dazu** (21.08.2026). Geflickt wurde es von Anfang an
 * (`aufgabeFlicken`), angehalten nicht – solange nur Listen optimistisch waren,
 * fiel das nicht auf. Seit die Planungsfelder im offenen Detailfenster flicken,
 * ist genau dieses Fenster die Stelle, an der eine verspätete Antwort sichtbar
 * würde: der Knopf spränge zurück auf die alte Stufe.
 */
async function anhalten(qc: QueryClient, aufgabeId: string): Promise<void> {
  await Promise.all([
    qc.cancelQueries({ queryKey: aufgabenQuery.queryKey }),
    qc.cancelQueries({ queryKey: poolsQuery.queryKey }),
    qc.cancelQueries({ queryKey: aufgabeDetailQuery(aufgabeId).queryKey }),
    /** Sonst bringt eine unterwegs befindliche Antwort die eben entfernte Zeile zurück. */
    qc.cancelQueries(EINGANG_LISTE),
  ])
}

/** Eine Aufgabe in beiden Zwischenspeichern ändern, in denen sie vorkommt. */
function aufgabeFlicken(qc: QueryClient, id: string, flicken: Flicken): void {
  qc.setQueryData(aufgabenQuery.queryKey, (alt: Aufgabe[] | undefined) =>
    alt?.map((a) => (a.id === id ? { ...a, ...flicken } : a)),
  )
  qc.setQueryData(aufgabeDetailQuery(id).queryKey, (alt: AufgabeDetailDaten | undefined) =>
    alt ? { ...alt, ...flicken } : alt,
  )
}

/**
 * Aufgabe aus **allen** Pools nehmen – so wie es Server bei Ziehen und Zuweisen
 * tut. Sie darf in mehreren liegen; gezogen wird sie einmal, und bliebe sie im
 * zweiten Pool stehen, würde sie ein zweites Mal angeboten.
 */
function ausPoolsNehmen(qc: QueryClient, id: string): void {
  qc.setQueryData(poolsQuery.queryKey, (alt: Pool[] | undefined) =>
    alt?.map((pool) => {
      const aufgaben = pool.aufgaben.filter((a) => a.id !== id)
      if (aufgaben.length === pool.aufgaben.length) return pool
      return {
        ...pool,
        aufgaben,
        // Neu bewerten, sonst bleibt die Limit-Warnung stehen, obwohl der Pool
        // wieder passt.
        ueberLimit: pool.limitAnzahl !== null && aufgaben.length > pool.limitAnzahl,
      }
    }),
  )
  qc.setQueryData(aufgabeDetailQuery(id).queryKey, (alt: AufgabeDetailDaten | undefined) =>
    alt ? { ...alt, poolIds: [] } : alt,
  )
}

/**
 * **Aus dem Eingang nehmen, sobald sie verteilt ist** (21.08.2026, Nils: *„wenn ich
 * da die Aufgaben bearbeite und dann schließe, bleiben die bis zum Reload in der
 * Liste"*).
 *
 * Der häufigste Weg durch den Eingang ist der von Hand – Aufgabe öffnen, Pool und
 * Zeitfenster setzen, schließen. Dabei wirkt jedes Feld sofort, nur die Liste
 * dahinter nicht: sie hängt an einer eigenen Abfrage, und die fragt niemand neu.
 *
 * **Nur in eine Richtung.** Ob eine Aufgabe wieder *hineingehört* – Pool entfernt,
 * Zeitfenster geleert –, entscheidet dieselbe Bedingung über drei Tabellen; das
 * hier zu raten hieße, `imEingang` ein zweites Mal zu schreiben. Der Rückweg
 * kommt über den Socket (`AppShell.tsx`), und er ist der seltene Fall.
 */
function ausEingangNehmen(qc: QueryClient, id: string): void {
  qc.setQueriesData<EingangZeile[]>(EINGANG_LISTE, (alt) => alt?.filter((a) => a.id !== id))
}

/**
 * **Nehmen: „das gehört mir"** (Kap. 4.3, [Z-06](../../../../docs/phasen/zusatz-06-nehmen-und-starten.md)).
 *
 * Vorhersage: die Aufgabe gehört mir und verlässt alle Pools – **der Status bleibt**. Genau
 * darin unterscheidet sie sich vom Starten, und genau deshalb sperrt das WIP-Limit sie nicht.
 *
 * Der häufigste Fehlerfall ist echt und erwartet: zwei Personen greifen nach derselben
 * Aufgabe, eine bekommt `CONFLICT`. Deshalb ist der Rücksetzer hier nicht die
 * Ausnahmebehandlung, sondern Teil des Ablaufs.
 */
export function useNehmen() {
  const qc = useQueryClient()

  return useMutation({
    /**
     * **Der Grund reist mit** (Phase 8, Nils am 04.08.2026).
     *
     * Wer eine Aufgabe nimmt, die sich jemand anders wünscht, kann das – muss es aber
     * begründen. Der Server verlangt es, diese Signatur reicht es durch, und die Begründung
     * landet im Ereignis-Log. Sie ist Material für den späteren Verteilautomatismus: *„Chris
     * war im Urlaub"* und *„ich hatte gerade Zeit"* führen zu verschiedenen Regeln.
     */
    mutationFn: ({ id, grund }: { id: string; grund?: string }) =>
      trpc.tasks.nehmen.mutate(grund ? { id, grund } : { id }),
    onMutate: async ({ id }) => {
      await anhalten(qc, id)
      const vorher = schnappschuss(qc, id)
      const ich = qc.getQueryData<Sitzung>(sitzungQuery.queryKey)

      aufgabeFlicken(qc, id, {
        assigneeId: ich?.id ?? null,
        assigneeName: ich?.displayName ?? null,
        /**
         * **`workAssigneeId` mit vorhersagen** – sonst stünde nach dem Klick zwar mein Name
         * an der Zeile, die Marke sagte aber weiter „herrenlos", und der Knopf hieße noch
         * „Nehmen". Die Vorhersage muss dieselbe Frage beantworten wie die Ansicht.
         */
        workAssigneeId: ich?.id ?? null,
        workTakenAt: new Date().toISOString(),
      })
      ausPoolsNehmen(qc, id)
      return vorher
    },
    onError: (_fehler, _eingabe, vorher) => zurueckrollen(qc, vorher),
  })
}

/**
 * **Starten: „ich mache das jetzt"** ([Z-06](../../../../docs/phasen/zusatz-06-nehmen-und-starten.md)).
 *
 * Vorhersage: `in_arbeit`, gehört mir, raus aus allen Pools. Der Aufruf kann beides – aus dem
 * Vorrat greifen und nur anfangen, was mir schon gehört –, und die Vorhersage deckt beide
 * Fälle mit denselben drei Zeilen ab: was schon so ist, ändert sich dabei nicht.
 */
export function useStarten() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ id, grund }: { id: string; grund?: string }) =>
      trpc.tasks.starten.mutate(grund ? { id, grund } : { id }),
    onMutate: async ({ id }) => {
      await anhalten(qc, id)
      const vorher = schnappschuss(qc, id)
      const ich = qc.getQueryData<Sitzung>(sitzungQuery.queryKey)

      /**
       * **`workTakenAt` nur, wenn es noch keins gibt** – dieselbe Regel wie im Server.
       *
       * Wer eine Aufgabe seit gestern hat und sie heute anfängt, hat sie **gestern** genommen.
       * Den Zeitpunkt hier blind zu überschreiben hieße: die Oberfläche zeigt eine Liegezeit von
       * null, der Server rechnet mit der echten – und die Vorhersage wäre falsch, sobald jemand
       * hinsieht.
       */
      const bisher = vorher.aufgaben?.find((a) => a.id === id)?.workTakenAt ?? null

      aufgabeFlicken(qc, id, {
        status: 'in_arbeit',
        assigneeId: ich?.id ?? null,
        assigneeName: ich?.displayName ?? null,
        workAssigneeId: ich?.id ?? null,
        workTakenAt: bisher ?? new Date().toISOString(),
      })
      ausPoolsNehmen(qc, id)
      return vorher
    },
    onError: (_fehler, _eingabe, vorher) => zurueckrollen(qc, vorher),
  })
}

/**
 * Status setzen (Kap. 4.7). Vorhersage: nur `status`.
 *
 * `quellStatus` bleibt bewusst unberührt – geschrieben wird `work_status`
 * ([E-24](../../../../docs/ENTSCHEIDUNGEN.md)), und der Hinweis „Work führt X,
 * GitLab steht auf Y" soll sofort richtig stehen und nicht erst nach dem
 * Nachladen.
 */
export function useStatusSetzen() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (v: {
      id: string
      status: TaskStatus
      notiz?: string
      /** Die Wiedervorlage beim Warten (E-315) – der Server räumt sie beim Verlassen weg. */
      wartenBis?: string | null
    }) => trpc.tasks.statusSetzen.mutate(v),
    onMutate: async ({ id, status, wartenBis }) => {
      await anhalten(qc, id)
      const vorher = schnappschuss(qc, id)
      /** Wie der Server: außerhalb von `warten` gibt es keine Wiedervorlage. */
      aufgabeFlicken(qc, id, {
        status,
        wartenBis: status === 'warten' ? (wartenBis ?? null) : null,
      })
      /** Erledigt und zurückgestellt fallen aus `imEingang` – siehe `ausEingangNehmen`. */
      if (status === 'erledigt' || status === 'zurueckgestellt') ausEingangNehmen(qc, id)
      /** Erledigt oder zur Abnahme – der Puls lässt die Aufgabe los, wie im Server. */
      pulsNachStatus(qc, id, status)
      return vorher
    },
    onError: (_fehler, _v, vorher) => zurueckrollen(qc, vorher),
    onSettled: (_d, _f, { status }) => {
      if (PULS_LOSLASSEN_BEI.includes(status)) void qc.invalidateQueries({ queryKey: ['puls'] })
    },
  })
}

/**
 * **Zurückgeben – die Umkehrung des Ziehens** (Nils, 18.08.2026: *„Wie würde ich die Aufgabe
 * wieder «zurück» tun, wenn ich die doch nicht machen kann/will?"*).
 *
 * Der Zustand davor – Pools und Status – steht im Ereignis-Log; der Server holt ihn von dort
 * ([E-132](../../../../docs/ENTSCHEIDUNGEN.md)). Deshalb **keine** optimistische Vorhersage:
 * die Ansicht weiß nicht, wohin die Aufgabe zurückfällt, und geraten wäre schlimmer als kurz
 * gewartet.
 *
 * Steht seit Z-02 hier statt in der Ansicht: „Mein Tag" ruft es an zwei Stellen auf, und zwei
 * gleichlautende `useMutation`-Blöcke laufen früher oder später auseinander.
 */
export function useZurueckgeben() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => trpc.tasks.zurueckgeben.mutate({ id }),
    onSuccess: async () => {
      await nebenBestandVerwerfen(qc)
      await qc.invalidateQueries({ queryKey: ['pools'] })
    },
  })
}

/**
 * Zuweisen (Kap. 4.3, AP 4.6). Vorhersage: neuer Bearbeiter, Aufgabe verlässt
 * alle Pools, **Status unverändert** – zugewiesen heißt „das ist deine", nicht
 * „du arbeitest dran".
 *
 * `personName` muss der Aufrufer mitgeben: der Server liefert ihn erst in der
 * Antwort, und bis dahin stünde in der Liste „niemand zugewiesen".
 */
export function useZuweisen() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (v: { id: string; personId: string | null; personName: string | null }) =>
      trpc.tasks.zuweisen.mutate({ id: v.id, personId: v.personId }),
    onMutate: async ({ id, personId, personName }) => {
      await anhalten(qc, id)
      const vorher = schnappschuss(qc, id)
      aufgabeFlicken(qc, id, { assigneeId: personId, assigneeName: personId ? personName : null })
      // Aufheben nimmt sie nicht aus den Pools – sie ist dann wieder ziehbar.
      if (personId) ausPoolsNehmen(qc, id)
      return vorher
    },
    onError: (_fehler, _v, vorher) => zurueckrollen(qc, vorher),
  })
}

/**
 * Was an den Planungsfeldern gesetzt werden kann. **Ein fehlendes Feld heißt
 * „nicht anfassen", `null` heißt „leeren"** – dieselbe Unterscheidung wie in
 * `tasks.planungSetzen`, und sie ist der Grund, warum hier nirgends `?? null`
 * steht: `null` ist eine Aussage.
 *
 * `wunschEntwickler` trägt **Namen**, obwohl der Server nur Kennungen bekommt.
 * Dieselbe Überlegung wie `personName` bei `useZuweisen`: die Namen stehen an
 * den Marken, und bis zur Antwort stünde dort sonst eine Kennung.
 */
export interface PlanungEingabe {
  zeitfenster?: Zeitfenster | null
  groesse?: Aufgabengroesse | null
  mitOwnerPersonId?: string | null
  /**
   * **„Hier nimmt niemand ab"** – der dritte Zustand (E-304). Reist zusammen mit
   * `mitOwnerPersonId`: `null` allein hieße „der des Vorhabens".
   */
  mitOwnerLeer?: boolean
  wunschEntwickler?: { id: string; name: string }[]
}

/**
 * **Planung an der Aufgabe: Zeitfenster, Größe, „kommen in Frage", „nimmt ab"**
 * (21.08.2026, nach Nils' Messung: *„Ich finde das Aufgabenpopup sehr langsam,
 * wenn man da was klickt – Zeitfenster, Pool, Aufgabenumfang: 2–3 Sekunden."*).
 *
 * Die Felder hingen bis hierher an `invalidateQueries(['tasks'])`. Das trifft
 * `tasks.all` – den **Gesamtbestand**, 994 ms und 3 MB (`AppShell.tsx`) –, und
 * weil `tasks.detail` im selben Stapel des `httpBatchLink` reist, sprang der
 * Knopf erst um, wenn der Bestand fertig geladen war. Ein Klick auf eine
 * Zeitfenster-Stufe wartete also auf 3.177 Aufgaben, um fünf Buchstaben
 * umzufärben.
 *
 * ## Die Vorhersage
 *
 * `zeitfensterAb` ist der Teil, den man leicht vergisst: der Server setzt den
 * Anker bei **jeder** Einstufung neu und löscht ihn beim Leeren mit. Ohne diese
 * Zeile stünde neben der neuen Stufe die Frist der alten – eine falsche Angabe,
 * die nach Tatsache aussieht.
 *
 * Was daraus **abgeleitet** ist, wird mit abgeleitet: `zeitfensterGeerbt` und
 * `zeitfensterAbWirksam` rechnet der Server aus denselben zwei Werten, die hier
 * schon im Zwischenspeicher liegen (Aufgabe schlägt Vorhaben, E-108).
 *
 * ## Die eine Ausnahme
 *
 * `mitOwnerPersonId` zieht `abnahmeDurchId` und `abnahmeDurchName` nach sich –
 * ein `COALESCE` über das Vorhaben, dessen Kennung im Detail gar nicht steht
 * (nur der Name). Beim Leeren ließe sich also nicht sagen, wer danach abnimmt,
 * und die Statusleiste hängt daran: eine geratene Abnahme zeigt einen
 * „Erledigt"-Knopf, den der Server abweist. Diese eine Änderung holt das Detail
 * deshalb nach – **nur das Detail**, nicht den Bestand.
 */
export function usePlanungSetzen(aufgabeId: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (v: PlanungEingabe) =>
      trpc.tasks.planungSetzen.mutate({
        id: aufgabeId,
        ...(v.zeitfenster !== undefined ? { zeitfenster: v.zeitfenster } : {}),
        ...(v.groesse !== undefined ? { groesse: v.groesse } : {}),
        ...(v.mitOwnerPersonId !== undefined ? { mitOwnerPersonId: v.mitOwnerPersonId } : {}),
        ...(v.mitOwnerLeer !== undefined ? { mitOwnerLeer: v.mitOwnerLeer } : {}),
        ...(v.wunschEntwickler ? { wunschPersonIds: v.wunschEntwickler.map((w) => w.id) } : {}),
      }),
    onMutate: async (v) => {
      await anhalten(qc, aufgabeId)
      const vorher = schnappschuss(qc, aufgabeId)
      /** Derselbe Stempel für beide Zwischenspeicher – zwei `new Date()` wären zwei Fristen. */
      const jetzt = new Date().toISOString()

      qc.setQueryData(
        aufgabeDetailQuery(aufgabeId).queryKey,
        (alt: AufgabeDetailDaten | undefined) =>
          alt
            ? {
                ...alt,
                ...(v.zeitfenster !== undefined
                  ? {
                      zeitfenster: v.zeitfenster,
                      zeitfensterAb: v.zeitfenster === null ? null : jetzt,
                      zeitfensterGeerbt: v.zeitfenster === null ? alt.vorhabenZeitfenster : null,
                      zeitfensterAbWirksam:
                        v.zeitfenster === null ? alt.vorhabenZeitfensterAb : jetzt,
                    }
                  : {}),
                ...(v.groesse !== undefined ? { groesse: v.groesse } : {}),
                ...(v.mitOwnerPersonId !== undefined
                  ? { mitOwnerPersonId: v.mitOwnerPersonId }
                  : {}),
                ...(v.mitOwnerLeer !== undefined ? { mitOwnerLeer: v.mitOwnerLeer } : {}),
                ...(v.wunschEntwickler ? { wunschEntwickler: v.wunschEntwickler } : {}),
              }
            : alt,
      )

      /**
       * Im Bestand nur, was dort **wortgleich** steht: `tasks.all` führt
       * `zeitfenster`, `zeitfensterAb` und `groesse` als eigene Spalten – die
       * Zeitachse und der Trichter sortieren danach. `wunschIds` und
       * `abnahmeDurchId` sind dort gerechnet und bleiben dem Socket überlassen.
       */
      qc.setQueryData(aufgabenQuery.queryKey, (alt: Aufgabe[] | undefined) =>
        alt?.map((a) =>
          a.id === aufgabeId
            ? {
                ...a,
                ...(v.zeitfenster !== undefined
                  ? {
                      zeitfenster: v.zeitfenster,
                      zeitfensterAb: v.zeitfenster === null ? null : jetzt,
                    }
                  : {}),
                ...(v.groesse !== undefined ? { groesse: v.groesse } : {}),
              }
            : a,
        ),
      )

      /**
       * Zwei der drei Eingangs-Kriterien liegen hier: ein gesetztes Zeitfenster und
       * jemand, der in Frage kommt. Beim *Leeren* bleibt die Zeile stehen – der
       * Rückweg gehört dem Socket (`ausEingangNehmen`).
       */
      if (v.zeitfenster || (v.wunschEntwickler && v.wunschEntwickler.length > 0)) {
        ausEingangNehmen(qc, aufgabeId)
      }

      return vorher
    },
    onSuccess: (_antwort, v) => {
      if (v.mitOwnerPersonId !== undefined || v.mitOwnerLeer !== undefined) {
        void qc.invalidateQueries({ queryKey: aufgabeDetailQuery(aufgabeId).queryKey })
      }
    },
    onError: (_fehler, _v, vorher) => zurueckrollen(qc, vorher),
  })
}

/**
 * **Pools setzen aus dem Aufgabendetail** (21.08.2026, siehe `usePlanungSetzen`).
 *
 * Vorhergesagt wird die Zugehörigkeit **an der Aufgabe** – das ist es, was die
 * Knöpfe im Detailfenster zeigen und was der Klick verspricht.
 *
 * **Der Pool-Zwischenspeicher bleibt bewusst unangetastet.** Eine Aufgabe aus
 * einem Pool zu nehmen wäre ein `filter` (`ausPoolsNehmen` tut genau das); sie
 * *hineinzulegen* hieße, eine Poolzeile zu erfinden – vierzehn Felder, darunter
 * `wunschNamen` und `inPools`, die der Server aus vier Quellen zusammenrechnet
 * (`pools.all`). Eine halb geratene Karte im Vorrat ist schlechter als eine, die
 * einen Wimpernschlag später erscheint: der Socket bringt sie ohnehin.
 */
export function usePoolsSetzen(aufgabeId: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (poolIds: string[]) => trpc.tasks.poolsSetzen.mutate({ id: aufgabeId, poolIds }),
    onMutate: async (poolIds) => {
      await anhalten(qc, aufgabeId)
      const vorher = schnappschuss(qc, aufgabeId)
      qc.setQueryData(
        aufgabeDetailQuery(aufgabeId).queryKey,
        (alt: AufgabeDetailDaten | undefined) => (alt ? { ...alt, poolIds } : alt),
      )
      /** Das dritte Kriterium: wer in einem Pool liegt, liegt nicht mehr im Eingang. */
      if (poolIds.length > 0) ausEingangNehmen(qc, aufgabeId)
      return vorher
    },
    onError: (_fehler, _v, vorher) => zurueckrollen(qc, vorher),
  })
}

/* ─────────────────────────── Auf dem Schirm (Z-03) ───────────────────────── */

/**
 * **Anpinnen, merken, abpinnen, stumm stellen** – vier Handlungen an einer Abfrage
 * ([Z-03](../../../../docs/phasen/zusatz-03-auf-dem-schirm.md)).
 *
 * Alle vier sind Standardaktionen und dürfen deshalb keinen Spinner zeigen (Leitplanke 1).
 * Vorhergesagt wird ausschließlich `['schirm', 'meins']` – die Pinnwand ist eine Liste, die
 * niemandem sonst gehört: kein Pool ändert sich, kein Status, kein Bestand. Das ist der
 * angenehmste Fall von optimistisch, den es hier gibt.
 *
 * **Was nicht vorhergesagt wird, ist die Warteliste.** Sie wird aus `wartekandidaten` gerechnet
 * (`lage.ts`), und die Rechnung liest die Pins – ein angepinnter Kandidat verschwindet also von
 * selbst aus „Ich warte", sobald der Pin im Zwischenspeicher steht. Zwei Vorhersagen für eine
 * Handlung wären zwei Stellen, an denen dieselbe Regel steht.
 */
type SchirmDaten = Schirm | undefined

/** Der Schlüssel eines Ziels – dieselbe Bildung wie in `@netcon-work/shared`. */
function zielGleich(a: Pinziel, b: Pinziel): boolean {
  return pinzielSchluessel(a) === pinzielSchluessel(b)
}

async function schirmAnhalten(qc: QueryClient): Promise<Schirm | undefined> {
  await qc.cancelQueries({ queryKey: schirmQuery.queryKey })
  return qc.getQueryData(schirmQuery.queryKey)
}

function schirmFlicken(qc: QueryClient, f: (alt: NonNullable<SchirmDaten>) => Schirm): void {
  qc.setQueryData(schirmQuery.queryKey, (alt: SchirmDaten) => (alt ? f(alt) : alt))
}

/**
 * Einen Pin anlegen oder ändern – im Zwischenspeicher wie auf dem Server: **eine** Zeile je
 * Ziel, gefunden über den Zielschlüssel.
 *
 * Die vorhergesagte Kennung ist ein Platzhalter (`vorlaeufig-…`). Sie wird nie geschrieben und
 * lebt nur bis zur nächsten Socket-Runde; sichtbar ist sie nirgends, weil die Zone über das
 * Ziel schlüsselt und nicht über die Kennung.
 */
function pinSetzenImSpeicher(
  qc: QueryClient,
  ziel: Pinziel,
  werte: Partial<Pick<Schirm['pins'][number], 'art' | 'notiz' | 'wiedervorlage'>>,
): void {
  schirmFlicken(qc, (alt) => {
    const vorhanden = alt.pins.find((p) => zielGleich(p.ziel, ziel))
    if (vorhanden) {
      return {
        ...alt,
        pins: alt.pins.map((p) => (zielGleich(p.ziel, ziel) ? { ...p, ...werte } : p)),
      }
    }
    return {
      ...alt,
      pins: [
        ...alt.pins,
        {
          id: `vorlaeufig-${pinzielSchluessel(ziel)}`,
          art: 'gepinnt' as const,
          ziel,
          notiz: null,
          wiedervorlage: null,
          /**
           * **Als Zeichenkette, nicht als `Date`.** Über die Leitung ist JSON, und tRPC leitet
           * genau das ab – ein `Date` im Zwischenspeicher wäre der einzige Wert der Liste, der
           * sich anders verhält als alle anderen, und der Unterschied fiele erst dort auf, wo
           * jemand ihn liest.
           */
          angelegtAm: new Date().toISOString(),
          ...werte,
        },
      ],
    }
  })
}

export function usePinnen() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (ziel: Pinziel) => trpc.schirm.pinnen.mutate({ ziel }),
    onMutate: async (ziel) => {
      const vorher = await schirmAnhalten(qc)
      pinSetzenImSpeicher(qc, ziel, { art: 'gepinnt' })
      return vorher
    },
    onError: (_fehler, _ziel, vorher) => qc.setQueryData(schirmQuery.queryKey, vorher),
  })
}

export function useAbpinnen() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (ziel: Pinziel) => trpc.schirm.abpinnen.mutate({ ziel }),
    onMutate: async (ziel) => {
      const vorher = await schirmAnhalten(qc)
      schirmFlicken(qc, (alt) => ({
        ...alt,
        pins: alt.pins.filter((p) => !zielGleich(p.ziel, ziel)),
      }))
      return vorher
    },
    onError: (_fehler, _ziel, vorher) => qc.setQueryData(schirmQuery.queryKey, vorher),
  })
}

/**
 * Notiz und Wiedervorlage in einem Zug.
 *
 * **Der Fehlerfall ist hier nicht selten, sondern erwartet:** eine Wiedervorlage ohne Notiz
 * weist der Server ab ([E-216](../../../../docs/ENTSCHEIDUNGEN.md#e-216)). Der Rücksetzer ist
 * deshalb kein Notnagel, sondern Teil des Ablaufs – wie bei `useNehmen` und dem Wettlauf um
 * dieselbe Aufgabe.
 */
export function useMerken() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (w: { ziel: Pinziel; notiz: string | null; wiedervorlage: string | null }) =>
      trpc.schirm.merken.mutate(w),
    onMutate: async (w) => {
      const vorher = await schirmAnhalten(qc)
      pinSetzenImSpeicher(qc, w.ziel, {
        art: 'gepinnt',
        notiz: w.notiz,
        wiedervorlage: w.wiedervorlage,
      })
      return vorher
    },
    onError: (_fehler, _w, vorher) => qc.setQueryData(schirmQuery.queryKey, vorher),
  })
}

/** „Erledigt sich" – die Wartezeile für immer stumm stellen. Nur für Aufgaben. */
export function useStumm() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (taskId: string) => trpc.schirm.stumm.mutate({ taskId }),
    onMutate: async (taskId) => {
      const vorher = await schirmAnhalten(qc)
      pinSetzenImSpeicher(
        qc,
        { art: 'aufgabe', taskId },
        { art: 'stumm', notiz: null, wiedervorlage: null },
      )
      return vorher
    },
    onError: (_fehler, _taskId, vorher) => qc.setQueryData(schirmQuery.queryKey, vorher),
  })
}

/* ─────────────────────────── Nach einer eigenen Aktion ───────────────────────── */

/**
 * **Alles unter `['tasks']` neu – außer dem Bestand selbst** (15.10.2026,
 * [E-410](../../../../docs/ENTSCHEIDUNGEN.md#e-410)).
 *
 * Hier stand an 20 Stellen `invalidateQueries({ queryKey: ['tasks'] })`. Das trifft auch
 * `['tasks', 'all']` – den ganzen Bestand, Ø 935 ms und rund 3 MB – und holte ihn nach **jeder**
 * eigenen Aktion neu. Gemessen über 46 Tage: 152 volle Bestandsabfragen am Tag. Dabei meldet der
 * Server jede geänderte Aufgabe über den Live-Kanal, und `AppShell` holt genau sie nach
 * (`bestandFlicken`, E-363). Das volle Nachladen war doppelt.
 *
 * **Nur, wenn der Live-Kanal steht.** Ist er getrennt, kommt keine Meldung – dann wird der Bestand
 * wie bisher ganz geholt. Und nur für Aktionen, die nachweislich melden; Sammelaktionen,
 * Rückgängig, Vorhaben-Änderungen, Outlook-Abgleich und Triage-Regeln laden weiter alles.
 */
export function nebenBestandVerwerfen(qc: QueryClient): Promise<void> {
  if (!liveKanalVerbunden()) return qc.invalidateQueries({ queryKey: ['tasks'] })
  return qc.invalidateQueries({
    queryKey: ['tasks'],
    predicate: (q) => q.queryKey[1] !== 'all',
  })
}
